import React, {
    useEffect,
    useRef,
    useState
} from 'react';

import {
    useDispatch,
    useSelector
} from 'react-redux';

import Swal from 'sweetalert2';

import {
    consultarLogoDocumentosAction,
    guardarLogoDocumentosAction,
    eliminarLogoDocumentosAction
} from '../../actions/logoDocumentosActions';

import {
    TarjetaAncha,
    CabeceraTarjeta,
    BloqueTituloTarjeta,
    TituloTarjeta,
    SubtituloTarjeta,
    DatoBloque,
    EtiquetaDato,
    ValorDato,
    PanelEdicion,
    InputCampo,
    TextoAyuda,
    TextoError,
    FilaAcciones,
    BotonPrimario,
    BotonSecundario,
    BotonPeligro
} from '../Css/StyledAdministrarPerfil';


const LogoDocumentos = ({config}) => {

    const dispatch = useDispatch();

    /*
    ====================================
    REDUX
    ====================================
    */

    const logoConfigurado = useSelector(
        state => state.usuario.logo_documentos_configurado
    );

    const logoCargando = useSelector(
        state => state.usuario.logo_documentos_cargando
    );

    const logoCargado = useSelector(
        state => state.usuario.logo_documentos_cargado
    );


    /*
    ====================================
    ESTADO LOCAL
    ====================================
    */

    const [archivo, setArchivo] = useState(null);

    const [vistaPrevia, setVistaPrevia] = useState('');

    const [errorArchivo, setErrorArchivo] = useState('');

    const [procesando, setProcesando] = useState(false);

    const [consultaIniciada, setConsultaIniciada] = useState(false);

    const archivoRef = useRef(null);

    const consultaRealizadaRef = useRef(false);


    /*
    ====================================
    CONSULTAR SI TIENE LOGO
    ====================================

    La consulta devuelve solamente
    logo_configurado: true / false.

    No descargamos la imagen guardada.
    ====================================
    */

    useEffect(() => {

        if (!config || consultaRealizadaRef.current) {
            return;
        }

        consultaRealizadaRef.current = true;

        setConsultaIniciada(true);

        dispatch(
            consultarLogoDocumentosAction({config})
        );

    }, [config, dispatch]);


    /*
    ====================================
    VISTA PREVIA LOCAL
    ====================================

    La vista previa se crea únicamente
    con el archivo seleccionado.

    No se solicita la imagen guardada
    al backend.
    ====================================
    */

    useEffect(() => {

        if (!archivo) {

            setVistaPrevia('');

            return;

        }

        const urlTemporal = URL.createObjectURL(archivo);

        setVistaPrevia(urlTemporal);

        return () => {

            URL.revokeObjectURL(urlTemporal);

        };

    }, [archivo]);


    /*
    ====================================
    SELECCIONAR ARCHIVO
    ====================================
    */

    const seleccionarArchivo = async (e) => {

        const archivoSeleccionado = e.target.files?.[0];

        setErrorArchivo('');
        setArchivo(null);

        if (!archivoSeleccionado) {
            return;
        }

        const mostrarError = async (mensaje) => {

            e.target.value = '';

            await Swal.fire({
                icon: 'error',
                title: 'No se pudo seleccionar el logo',
                text: mensaje,
                confirmButtonText: 'Entendido'
            });

        };

        if (
            ![
                'image/png',
                'image/jpeg'
            ].includes(archivoSeleccionado.type)
        ) {

            await mostrarError(
                'Seleccioná una imagen PNG o JPG.'
            );

            return;

        }

        if (archivoSeleccionado.size > 500 * 1024) {

            await mostrarError(
                'El logo no puede superar los 500 KB.'
            );

            return;

        }

        /*
        ====================================
        VALIDAR DIMENSIONES
        ====================================
        */

        const urlTemporal = URL.createObjectURL(
            archivoSeleccionado
        );

        try {

            const dimensiones = await new Promise(
                (resolve, reject) => {

                    const imagen = new Image();

                    imagen.onload = () => {

                        resolve({
                            ancho: imagen.naturalWidth,
                            alto: imagen.naturalHeight
                        });

                    };

                    imagen.onerror = () => {

                        reject(
                            new Error(
                                'No se pudo leer la imagen seleccionada.'
                            )
                        );

                    };

                    imagen.src = urlTemporal;

                }
            );

            if (
                dimensiones.ancho > 1200 ||
                dimensiones.alto > 1200
            ) {

                await mostrarError(
                    'El logo no puede superar los 1200 × 1200 píxeles.'
                );

                return;

            }

            setArchivo(archivoSeleccionado);

        } catch (error) {

            await mostrarError(
                error.message ||
                'No se pudo leer la imagen seleccionada.'
            );

        } finally {

            URL.revokeObjectURL(urlTemporal);

        }

    };


    /*
    ====================================
    LIMPIAR SELECCIÓN
    ====================================
    */

    const limpiarSeleccion = () => {

        setArchivo(null);

        setErrorArchivo('');

        if (archivoRef.current) {

            archivoRef.current.value = '';

        }

    };


    /*
    ====================================
    GUARDAR O REEMPLAZAR LOGO
    ====================================
    */

    const guardarLogo = async () => {

        if (!archivo || !config || procesando) {
            return;
        }

        setProcesando(true);

        const guardadoCorrectamente = await dispatch(
            guardarLogoDocumentosAction({
                config,
                archivo
            })
        );

        setProcesando(false);

        if (guardadoCorrectamente) {

            limpiarSeleccion();

            await Swal.fire({
                icon: 'success',
                title: 'Logo guardado',
                text: 'Tu logo se utilizará en los próximos documentos que generes.',
                confirmButtonText: 'Entendido'
            });

        }

    };


    /*
    ====================================
    ELIMINAR LOGO
    ====================================
    */

    const eliminarLogo = async () => {

        if (!config || procesando) {
            return;
        }

        const confirmacion = await Swal.fire({
            icon: 'warning',
            title: '¿Eliminar logo personalizado?',
            text: 'Los próximos documentos se generarán sin logo.',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true
        });

        if (!confirmacion.isConfirmed) {
            return;
        }

        setProcesando(true);

        const eliminadoCorrectamente = await dispatch(
            eliminarLogoDocumentosAction({
                config
            })
        );

        setProcesando(false);

        if (eliminadoCorrectamente) {

            limpiarSeleccion();

            await Swal.fire({
                icon: 'success',
                title: 'Logo eliminado',
                text: 'Tu logo personalizado se eliminó correctamente. Los próximos documentos se generarán sin logo.',
                confirmButtonText: 'Entendido'
            });

        }

    };


    /*
    ====================================
    RENDER
    ====================================
    */

    return (

        <TarjetaAncha>

            <CabeceraTarjeta>

                <BloqueTituloTarjeta>

                    <TituloTarjeta>
                        Logo para documentos
                    </TituloTarjeta>

                    <SubtituloTarjeta>
                        Si querés, podés agregar tu logo al encabezado de los documentos
                        que generás desde YÖGEN. Si no cargás uno, se generarán sin logo.
                    </SubtituloTarjeta>

                </BloqueTituloTarjeta>

            </CabeceraTarjeta>


            {
                (logoCargando || !consultaIniciada) && (

                    <TextoAyuda>
                        Consultando el estado de tu logo...
                    </TextoAyuda>

                )
            }


            {
                consultaIniciada &&
                !logoCargando &&
                !logoCargado && (

                    <TextoError>
                        No se pudo confirmar si tenés un logo
                        configurado. Recargá Mi perfil para
                        volver a intentarlo.
                    </TextoError>

                )
            }


            {
                logoCargado && (

                    <>

                        <DatoBloque>

                            <EtiquetaDato>
                                Estado actual
                            </EtiquetaDato>

                            <ValorDato $destacado>
                                {
                                    logoConfigurado
                                        ?
                                        <>
                                            <span style={{ color: '#00a161' }}>
                                                ✓ Logo personalizado guardado
                                            </span>

                                            <div style={{ marginTop: '5px', fontWeight: 400 }}>
                                                Tu logo se utilizará automáticamente en los
                                                próximos documentos que generes.
                                            </div>
                                        </>
                                        :
                                        'No tenés un logo configurado. Tus documentos se generarán sin logo.'
                                }
                            </ValorDato>

                        </DatoBloque>


                        <PanelEdicion>

                            <TextoAyuda>

                                Seleccioná una imagen PNG o JPG
                                de hasta 500 KB.

                                Podés revisar cómo se verá antes
                                de guardarla. Si ya tenés un logo,
                                el nuevo archivo lo reemplazará.

                            </TextoAyuda>


                            <InputCampo

                                ref={archivoRef}

                                type="file"

                                accept="image/png,image/jpeg"

                                onChange={seleccionarArchivo}

                                disabled={procesando}

                            />


                            {
                                errorArchivo && (

                                    <TextoError>
                                        {errorArchivo}
                                    </TextoError>

                                )
                            }


                            {
                                vistaPrevia && (

                                    <DatoBloque>

                                        <EtiquetaDato>
                                            Vista previa del nuevo logo
                                        </EtiquetaDato>

                                        <img

                                            src={vistaPrevia}

                                            alt="Vista previa del logo seleccionado"

                                            style={{

                                                display: 'block',

                                                maxWidth: '100%',

                                                maxHeight: '130px',

                                                objectFit: 'contain',

                                                marginTop: '12px'

                                            }}

                                        />

                                    </DatoBloque>

                                )
                            }


                            {
                                archivo && (

                                    <FilaAcciones>

                                        <BotonSecundario

                                            type="button"

                                            onClick={limpiarSeleccion}

                                            disabled={procesando}

                                        >

                                            Cancelar

                                        </BotonSecundario>


                                        <BotonPrimario

                                            type="button"

                                            onClick={guardarLogo}

                                            disabled={procesando}

                                        >

                                            {
                                                procesando
                                                    ?
                                                    'Guardando...'
                                                    :
                                                    logoConfigurado
                                                        ?
                                                        'Reemplazar logo'
                                                        :
                                                        'Guardar logo'
                                            }

                                        </BotonPrimario>

                                    </FilaAcciones>

                                )
                            }


                            {
                                logoConfigurado && !archivo && (

                                    <FilaAcciones>

                                        <BotonPeligro

                                            type="button"

                                            onClick={eliminarLogo}

                                            disabled={procesando}

                                        >

                                            {
                                                procesando
                                                    ?
                                                    'Eliminando...'
                                                    :
                                                    'Eliminar logo personalizado'
                                            }

                                        </BotonPeligro>

                                    </FilaAcciones>

                                )
                            }

                        </PanelEdicion>

                    </>

                )
            }

        </TarjetaAncha>

    );

};


export default LogoDocumentos;